/**
 * Set-name cache for the Set combobox in the Sealed / Card edit forms.
 *
 * PRIMARY SOURCE (2026-07-18): our OWN catalog — sealed_groups, synced nightly
 * from TCGCSV for ALL games. Names are normalized to the canonical plain style
 * (era prefixes stripped) and ordered newest-first by published_on. This
 * replaced the old per-game external APIs, which had gone stale (pokemontcg.io
 * was missing new sets like "Pitch Black") and a hand-curated One Piece list
 * (frozen at OP-11, missing "Heroines" — set lists now update themselves).
 *
 * The old external fetches (Pokemon API / Scryfall / Lorcast / curated lists)
 * survive only as fallbacks when the catalog returns nothing (offline, signed
 * out — catalog RLS is authenticated-only).
 *
 * Results live in localStorage for 24h. Concurrent fetches dedupe.
 */
import { sb } from "./supabase";
import { GAME_TO_CATEGORY } from "./catalogResolve";
import { normalizeSetName } from "./setNames";

const FALLBACK_SETS: Record<string, string[]> = {
  "Pokemon": [],
  "Magic: The Gathering": [],
  "Lorcana": ["The First Chapter", "Rise of the Floodborn", "Into the Inklands", "Ursula's Return", "Shimmering Skies", "Azurite Sea", "Archazia's Island", "Reign of Jafar"],
  "One Piece": ["Romance Dawn (OP-01)", "Paramount War (OP-02)", "Pillars of Strength (OP-03)", "Kingdoms of Intrigue (OP-04)", "Awakening of the New Era (OP-05)", "Wings of the Captain (OP-06)", "500 Years in the Future (OP-07)", "Two Legends (OP-08)", "Emperors in the New World (OP-09)", "Royal Blood (OP-10)", "A Fist of Divine Speed (OP-11)"],
  "Other": [],
};

// v2: cache key bumped 2026-07-18 so everyone's stale v1 lists (missing new
// sets) are abandoned immediately; TTL down to 24h — the source is our own DB
// now, so a daily refresh is cheap and tracks the nightly TCGCSV sync.
const KEY = "enigma_set_cache_v2";
const TTL_MS = 24 * 60 * 60 * 1000;

interface CacheEntry { sets: string[]; lastFetched: number; }
type CacheShape = Record<string, CacheEntry>;

const _inflight: Record<string, Promise<string[]> | undefined> = {};

function load(): CacheShape {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); }
  catch { return {}; }
}
function save(c: CacheShape): void {
  localStorage.setItem(KEY, JSON.stringify(c));
}

/** Primary source: sealed_groups (our TCGCSV-synced catalog), newest first,
 *  normalized + deduped. Returns [] for games with no catalog category. */
async function fetchSetsFromCatalog(game: string): Promise<string[]> {
  const cat = GAME_TO_CATEGORY[game];
  if (!cat) return [];
  const { data, error } = await sb
    .from("sealed_groups")
    .select("name,published_on")
    .eq("category_id", cat)
    .order("published_on", { ascending: false, nullsFirst: false })
    .limit(1000);
  if (error) throw error;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const g of (data || []) as Array<{ name: string | null }>) {
    const n = normalizeSetName(String(g.name || "")).trim();
    if (n && !seen.has(n)) { seen.add(n); out.push(n); }
  }
  return out;
}

async function fetchSetsForGame(game: string): Promise<string[]> {
  // Catalog first — self-updating for every synced game.
  try {
    const fromCatalog = await fetchSetsFromCatalog(game);
    if (fromCatalog.length) return fromCatalog;
  } catch (e) {
    console.warn("[sets] catalog fetch failed for", game, "— falling back:", e);
  }
  // Legacy external fallbacks below (offline / signed-out / unsynced games).
  if (game === "Pokemon") {
    const r = await fetch("https://api.pokemontcg.io/v2/sets?pageSize=250&orderBy=-releaseDate");
    if (!r.ok) throw new Error("HTTP " + r.status);
    const j = await r.json();
    return [...new Set((j.data || []).map((s: any) => (s.name || "").trim()).filter(Boolean))] as string[];
  }
  if (game === "Magic: The Gathering") {
    const r = await fetch("https://api.scryfall.com/sets");
    if (!r.ok) throw new Error("HTTP " + r.status);
    const j = await r.json();
    const skip = new Set(["token", "memorabilia", "funny", "minigame", "alchemy"]);
    const names = (j.data || [])
      .filter((s: any) => !skip.has(s.set_type))
      .map((s: any) => (s.name || "").trim())
      .filter(Boolean);
    return [...new Set(names)] as string[];
  }
  if (game === "Lorcana") {
    const r = await fetch("https://api.lorcast.com/v0/sets");
    if (!r.ok) throw new Error("HTTP " + r.status);
    const j = await r.json();
    const arr = j.results || j.data || j.sets || [];
    return [...new Set((arr as any[]).map(s => (s.name || "").trim()).filter(Boolean))] as string[];
  }
  // One Piece + Other → fallback list
  return FALLBACK_SETS[game] || [];
}

export const SetCache = {
  get(game: string): string[] {
    const c = load();
    const cached = c[game]?.sets;
    if (cached && cached.length) return cached;
    return FALLBACK_SETS[game] || [];
  },
  isStale(game: string): boolean {
    const c = load();
    const e = c[game];
    if (!e?.lastFetched) return true;
    return (Date.now() - e.lastFetched) > TTL_MS;
  },
  ensureFresh(game: string): Promise<string[]> {
    if (!this.isStale(game)) return Promise.resolve(this.get(game));
    if (_inflight[game]) return _inflight[game];
    _inflight[game] = (async () => {
      try {
        const sets = await fetchSetsForGame(game);
        if (sets && sets.length) {
          const c = load();
          c[game] = { sets, lastFetched: Date.now() };
          save(c);
        }
      } catch (e) {
        console.warn("Set fetch failed for", game, e);
      } finally {
        delete _inflight[game];
      }
      return this.get(game);
    })();
    return _inflight[game];
  },
};
